import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { Alert, Badge, LoadingBlock, PageContainer, PageHeader, Stack } from '../../components/ui';
import { getBrand } from '../../constants/brands';
import { getSiteProductKind } from '../../constants/siteConfig';
import { getCurrentRuntimeTarget } from '../../../config/runtime-target';
import { writeHomePageCache } from '../../../config/homePageCache';
import {
  HOME_PAGE_SETTINGS,
  MY_BRAND,
  UPDATE_BRAND_SETTINGS,
} from '../../services/brandService/queries';
import {
  defaultHomeWords,
  effectiveHomeWords,
  sanitizeHomePage,
} from '../../utils/homePageSettings';
import { BannerCard } from './BannerCard';
import { HomeWordsCard, PhonePreview } from './HomeWordsCard';
import { HomePagePatch, serverFieldErrors } from './pageSettingsForm';

type SavingPart = 'overlay' | 'web' | 'app' | null;

/** Which server the page edits (spec 029 FR-013): store apps read production. */
function appServerNote(target: 'development' | 'staging' | 'production'): string {
  if (target === 'production') {
    return 'This is the production server, which the store apps read.';
  }
  const name = target === 'staging' ? 'the staging server' : 'this development server';
  return `This is ${name}: only apps connected to it see the change. Store apps read production.`;
}

/**
 * Brand admin page for the homepage: banner photo and overlay, the website
 * headline and the app's home words (spec 029). Every value is stored on the
 * viewing brand only; an unset value keeps the built-in default.
 */
export default function AdminPageSettingsPage() {
  const skin = getBrand();
  const { data, loading, error } = useQuery(MY_BRAND, { fetchPolicy: 'network-only' });
  const [updateBrandSettings] = useMutation(UPDATE_BRAND_SETTINGS, {
    refetchQueries: [{ query: MY_BRAND }, { query: HOME_PAGE_SETTINGS }],
  });
  const [savingPart, setSavingPart] = useState<SavingPart>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | null>(null);

  const brand = data?.myBrand;
  const saved = sanitizeHomePage(brand?.homePage);
  const defaults = defaultHomeWords(skin);
  const words = effectiveHomeWords(skin, saved);
  // Stays and services sites have their own homepages (spec 029 Edge Cases).
  const carSite = getSiteProductKind() === 'car';

  /** The homepage reads this browser's cache first, so a save updates it too (US1-5). */
  const remember = (updated: any) => {
    if (!updated) return;
    writeHomePageCache(skin.brandSite, {
      homeBannerImageKey: updated.homeBannerImageKey ?? null,
      homePage: updated.homePage ?? null,
    });
  };

  const save = async (input: Record<string, unknown>) => {
    const result = await updateBrandSettings({ variables: { input } });
    remember(result.data?.updateBrandSettings);
  };

  const saveBannerKey = async (homeBannerImageKey: string | null, success: string) => {
    await save({ homeBannerImageKey });
    toast.success(success);
  };

  const saveHomePage =
    (part: Exclude<SavingPart, null>, success: string) => async (patch: HomePagePatch) => {
      if (!Object.keys(patch).length) return;
      setSavingPart(part);
      setFieldErrors(null);
      try {
        await save({ homePage: patch });
        toast.success(success);
      } catch (err: any) {
        const fields = serverFieldErrors(err);
        if (fields) setFieldErrors(fields);
        else toast.error(err?.message || 'Could not save the page settings');
      } finally {
        setSavingPart(null);
      }
    };

  return (
    <PageContainer width="lg" canvas>
      <PageHeader
        eyebrow="Admin"
        title={
          <>
            Page settings{' '}
            {brand?.brandSite && (
              <Badge tone="brand" size="md">
                {brand.brandSite}
              </Badge>
            )}
          </>
        }
        description={`The homepage of ${
          brand?.brandSite ?? 'this brand'
        } on the website and in the app. Other brands keep their own.`}
      />

      {loading && <LoadingBlock label="Loading page settings" />}
      {error && (
        <Alert tone="error" title="Could not load page settings">
          {error.message}
        </Alert>
      )}

      {!loading && !error && (
        <Stack>
          <BannerCard
            bannerKey={brand?.homeBannerImageKey}
            saved={saved}
            words={words}
            showOverlay={carSite}
            savingOverlay={savingPart === 'overlay'}
            overlayServerErrors={fieldErrors}
            onSaveBannerKey={saveBannerKey}
            onSaveOverlay={saveHomePage('overlay', 'Banner overlay saved')}
          />

          {carSite && (
            <HomeWordsCard
              testId="page-settings-web"
              title="Website headline"
              description="The title and subtitle on the homepage banner. Leave a box at its default to follow the standard words."
              fields={[
                { field: 'webTitle', label: 'Title' },
                { field: 'webSubtitle', label: 'Subtitle', multiline: true },
              ]}
              saved={saved}
              defaults={defaults}
              saving={savingPart === 'web'}
              serverErrors={fieldErrors}
              onSave={saveHomePage('web', 'Website headline saved')}
            />
          )}

          {carSite && (
            <HomeWordsCard
              testId="page-settings-app"
              title="Mobile app home"
              description="The title and subtitle at the top of the app's home screen."
              fields={[
                { field: 'appTitle', label: 'Title' },
                { field: 'appSubtitle', label: 'Subtitle', multiline: true },
              ]}
              saved={saved}
              defaults={defaults}
              saving={savingPart === 'app'}
              serverErrors={fieldErrors}
              note={`The apps show this the next time they open or come back to the foreground. ${appServerNote(
                getCurrentRuntimeTarget(),
              )}`}
              renderPreview={form => (
                <PhonePreview
                  title={form.appTitle?.trim() || defaults.appTitle}
                  subtitle={form.appSubtitle?.trim() || defaults.appSubtitle}
                />
              )}
              onSave={saveHomePage('app', 'App home saved')}
            />
          )}
        </Stack>
      )}
    </PageContainer>
  );
}
